import React, { useEffect, useState } from 'react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import FormInput from '../../shared/components/ui/FormInput';
import Button from '../../shared/components/ui/Button';
import {
  getAllInventoryByProjectId,
  createInventory,
  getMyVendors,
  createRFQ,
  updateInventory,
  downloadStockTemplate,
  uploadStockTemplate,
  getAllCategories
} from '../services/api/implementation/impl';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import toast from 'react-hot-toast';
import Loader from '../../shared/components/ui/Loader';
import noDataIcon from '../../shared/assets/png/noDataFound.png';
import { Pencil, Trash, Plus, CirclePlus, Download, Upload, ChevronDown, IndianRupee } from 'lucide-react';
import StockEntriesModal from '../components/inventory/StockEntriesModal';
import Modal from '../../shared/components/ui/Modal';
import Multiselect from 'multiselect-react-dropdown';
import RFQForm from '../components/forms/RFQForm';
import { createPortal } from 'react-dom';
import { useProjectsList } from '../../../contexts/ProjectsContext';
import { AddMaterials } from '../components/forms/AddMaterials';
import { useLocation } from 'react-router-dom';
import { resolveReturnTo, useSmartBack } from '../../shared/navigation';

const Stock = () => {
  const location = useLocation();
  const hasReturnTo = Boolean(resolveReturnTo(location));
  const { onBack } = useSmartBack({ fallback: '/builder/inventory' });
  const { collapsed } = useSidebar();
  const { projects, projectsForDropdown, isProjectsLoading, getProjectById } = useProjectsList();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isRFQModalOpen, setIsRFQModalOpen] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [itemQuantities, setItemQuantities] = useState({});
  const [selectedItemsForRFQ, setSelectedItemsForRFQ] = useState([]);
  const [stockCriticality, setStockCriticality] = useState('');
  const [daysFilter, setDaysFilter] = useState('90'); // Default to 90 days
  const [openDropdown, setOpenDropdown] = useState(null);
  const stockCriticalityHeaderRef = React.useRef(null);
  const daysFilterHeaderRef = React.useRef(null);
  const [OpenAddMaterial, setOpenAddMaterial] = useState(false);
  const [stockCriticalityDropdownRect, setStockCriticalityDropdownRect] = useState(null);
  const [daysFilterDropdownRect, setDaysFilterDropdownRect] = useState(null);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm();
  const projectId = watch('project');
  const categoryId = watch('category');

  const {
    data: AllInventories,
    refetch: fetchInventory,
    isLoading: isLoadingInventory
  } = useQuery({
    queryKey: ['AllInventories', projectId, categoryId, stockCriticality, daysFilter],
    queryFn: () => getAllInventoryByProjectId(projectId, categoryId, stockCriticality, daysFilter),
    enabled: !!projectId
  });

  const {
    data: AllCategories,
    isLoading: isLoadingCategories,
    refetch: refetchCategories
  } = useQuery({
    queryKey: ['AllCategories'],
    queryFn: () => getAllCategories(1, 500)
  });
  useEffect(() => {
    refetchCategories();
  }, []);

  const {
    data: allVendors,
    isLoading: isLoadingVendors,
    refetch: refetchVendors
  } = useQuery({
    queryKey: ['allVendors'],
    queryFn: () => getMyVendors(1, 500)
  });

  const transformedDataCategories =
    AllCategories?.data?.map((item) => ({
      label: item.name || '',
      id: item.id,
      value: item.id
    })) || [];

  useEffect(() => {
    if (projects) {
      // Get saved project from localStorage
      const savedProject = localStorage.getItem('stock_selected_project');
      const savedCategory = localStorage.getItem('stock_selected_category');
      const stateProjectId = location.state?.projectID;

      // Priority: navigation state (e.g. Create Inventory task) → saved → first project
      let projectToSet = projects?.[0]?.id;
      if (
        stateProjectId != null &&
        projects?.find((p) => p.id === parseInt(stateProjectId, 10))
      ) {
        projectToSet = parseInt(stateProjectId, 10);
      } else if (
        savedProject &&
        projects?.find((p) => p.id === parseInt(savedProject, 10))
      ) {
        projectToSet = parseInt(savedProject, 10);
      }

      setValue('project', projectToSet);

      // Set category if saved category exists
      if (savedCategory) {
        setValue('category', savedCategory);
      }
    }
  }, [projects, setValue, location.state?.projectID]);

  useEffect(() => {
    if (projectId) {
      fetchInventory();
      // Save selected project to localStorage
      localStorage.setItem('stock_selected_project', projectId.toString());
    }
  }, [projectId]);

  useEffect(() => {
    if (categoryId) {
      // Save selected category to localStorage
      localStorage.setItem('stock_selected_category', categoryId.toString());
    } else if (categoryId === '') {
      // Clear category from localStorage when "All Categories" is selected
      localStorage.removeItem('stock_selected_category');
    }
  }, [categoryId]);

  const formatDate = (dateString) => {
    if (!dateString) return null;

    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' }); // May, Jun, etc.
    const year = date.getFullYear();

    // Add "st", "nd", "rd", "th"
    const getOrdinal = (n) => {
      if (n > 3 && n < 21) return 'th'; // 11th, 12th, 13th
      switch (n % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    };

    return `${day}${getOrdinal(day)} ${month} ${year}`;
  };

  // Handle individual item selection
  const handleItemSelect = (itemId) => {
    setSelectedItems((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedItems.length === AllInventories?.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(AllInventories?.map((item) => item.id) || []);
    }
  };

  // Check if all items are selected
  const isAllSelected =
    AllInventories?.length > 0 && selectedItems.length === AllInventories?.length;

  // Get selected items count for display
  const selectedItemsCount = selectedItems.length;

  // Get unique category IDs from selected items
  const selectedItemCategoryIds = [
    ...new Set(selectedItemsForRFQ.map((item) => item.parent_category_id))
  ];

  // Filter vendors based on selected items' categories
  const filteredVendors =
    allVendors?.data?.filter((vendor) => {
      // Check if vendor has category mappings
      if (!vendor?.vendorCategoryMapping || vendor?.vendorCategoryMapping?.length === 0) {
        return false;
      }

      // Check if vendor has any category that matches the selected items' categories
      const vendorCategoryIds = vendor?.vendorCategoryMapping?.map(
        (mapping) => mapping.category_id
      );
      return selectedItemCategoryIds.some((categoryId) => vendorCategoryIds.includes(categoryId));
    }) || [];

  // Transform filtered vendors for dropdown
  const transformedVendors = filteredVendors?.map((vendor) => {
    // Get matching categories for this vendor
    const matchingCategories = vendor?.vendorCategoryMapping
      .filter((mapping) => selectedItemCategoryIds.includes(mapping.category_id))
      .map((mapping) => mapping.categroy?.name)
      .filter(Boolean);

    const categoryText = matchingCategories.length > 0 ? ` (${matchingCategories.join(', ')})` : '';

    return {
      name: `${vendor?.user?.name || ''}${categoryText}`,
      id: vendor?.id
    };
  });

  // Handle RFQ modal open
  const handleRFQClick = (item, event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!item || !item.id) {
      console.error('Invalid item for RFQ');
      return;
    }

    // If there are selected items, use those; otherwise use the clicked item
    const itemsToShow =
      selectedItems.length > 0
        ? AllInventories.filter((task) => selectedItems.includes(task.id))
        : [item];

    setSelectedItemsForRFQ(itemsToShow);

    // Initialize quantities for all items with stock_to_be_ordered if not negative
    const initialQuantities = {};
    itemsToShow.forEach((task) => {
      const stockToBeOrdered = parseInt(task.stock_to_be_ordered) || 0;
      // Pre-populate with stock_to_be_ordered only if it's not negative
      const initialQuantity = stockToBeOrdered > 0 ? stockToBeOrdered : '';
      initialQuantities[task.id] = itemQuantities[task.id] || initialQuantity;
    });
    setItemQuantities(initialQuantities);

    setIsRFQModalOpen(true);
  };

  // Handle vendor selection
  const handleVendorSelect = (selectedList) => {
    const vendorIds = selectedList.map((vendor) => vendor.id);
    setSelectedVendors(vendorIds);
  };

  // Handle quantity change
  const handleQuantityChange = (itemId, quantity) => {
    setItemQuantities((prev) => ({
      ...prev,
      [itemId]: quantity
    }));
  };

  // Handle RFQ submit
  const handleRFQSubmit = async (formPayload) => {
    // Validate vendor selection
    if (selectedVendors.length === 0) {
      toast.error('Please select at least one vendor');
      return;
    }

    // Validate quantities
    const invalidItems = selectedItemsForRFQ.filter((item) => {
      const quantity = itemQuantities[item.id];
      return !quantity || quantity <= 0;
    });

    if (invalidItems.length > 0) {
      toast.error('Please enter a quantity greater than 0 for all items');
      return;
    }

    const payload = {
      project_id: parseInt(projectId),
      ...formPayload
    };

    console.log('RFQ Payload:', payload);
    // Add your API call here
    try {
      const response = await createRFQ(payload);
      toast.success('RFQ submitted successfully');
      setIsRFQModalOpen(false);
      setSelectedVendors([]);
      setItemQuantities({});
      setSelectedItemsForRFQ([]);
    } catch (error) {
      console.error('Error creating RFQ:', error);
      toast.error(error.response?.data?.message || error?.message || 'Error creating RFQ');
    }
  };

  // Handle RFQ cancel
  const handleRFQCancel = () => {
    setIsRFQModalOpen(false);
    setSelectedVendors([]);
    setItemQuantities({});
    setSelectedItemsForRFQ([]);
  };

  // Check if submit button should be disabled
  const isSubmitDisabled = () => {
    // Check if vendors are selected
    if (selectedVendors.length === 0) return true;

    // Check if all items have valid quantities
    const hasInvalidQuantities = selectedItemsForRFQ.some((item) => {
      const quantity = itemQuantities[item.id];
      return !quantity || quantity <= 0;
    });

    return hasInvalidQuantities;
  };

  const { mutate: createInventoryfn } = useMutation({
    mutationFn: (payload) => createInventory(payload),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      toast.success('Stock created successfully');
      await fetchInventory();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error creating new stock', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error creating stock');
    }
  });

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [stockEntriesItem, setStockEntriesItem] = useState(null);
  const {
    register: editRegister,
    handleSubmit: handleEditSubmit,
    setValue: setEditValue,
    reset: resetEditForm,
    formState: { errors: editErrors }
  } = useForm();

  // Open edit modal and prefill values
  const handleEditClick = (item) => {
    setEditItem(item);
    setIsEditModalOpen(true);
    // Prefill all fields
    setEditValue('avg_acquiring_days', item.avg_acquiring_days || 0);
    setEditValue('safetyNet', item.safetyNet || 0);

    setEditValue('minimumQuantity', item.minimumQuantity || 0);
    setEditValue('physicalStock', item.physicalStock || 0);
  };

  // Handle edit modal close
  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setEditItem(null);
    resetEditForm();
  };

  // Edit form submit
  const onEditSubmit = async (data) => {
    if (!editItem) return;
    if (data.safetyNet > 100) {
      toast.error('Safety Net cannot be greater than 100%');
      return;
    }
    setIsSubmitting(true);
    try {
      // All fields are required and must be numbers >= 0
      const payload = {
        avg_acquiring_days: String(data.avg_acquiring_days),
        safetyNet: String(data.safetyNet),
        minimumQuantity: String(data.minimumQuantity),
        physicalStock: String(data.physicalStock)
      };
      await updateInventory(editItem.id, payload);
      toast.success('Inventory updated successfully');
      handleEditModalClose();
      await fetchInventory();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Error updating inventory');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    if (projectId) {
      try {
        const response = await downloadStockTemplate(projectId); // should return a
        console.log('response', response);
        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        // Find the project name
        const project = projects?.find((p) => p.id === projectId || p.value === projectId);
        const projectName = project?.name || project?.label || 'project';
        // Sanitize project name for filename
        const safeProjectName = projectName?.replace(/[^a-zA-Z0-9-_]/g, '_');
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${safeProjectName}_Inventory.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        toast.error(
          error?.response?.data?.message || error?.message || 'Failed to download template'
        );
      }
    } else {
      toast.error('Please select a project');
    }
  };

  const handleUploadTemplate = async () => {
    if (projectId) {
      // Create a file input element
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.xlsx,.xls';
      fileInput.style.display = 'none';

      // Add event listener for file selection
      fileInput.onchange = async (event) => {
        const file = event.target.files[0];
        if (file) {
          try {
            setIsSubmitting(true);
            const formData = new FormData();
            formData.append('file', file);

            const response = await uploadStockTemplate(projectId, formData);
            console.log('Uploaded template', response);
            toast.success('Template uploaded successfully');
            await fetchInventory();
          } catch (error) {
            console.error('Upload error:', error);
            toast.error(
              error?.response?.data?.message || error?.message || 'Failed to upload template'
            );
          } finally {
            setIsSubmitting(false);
          }
        }

        // Clean up the file input
        document.body.removeChild(fileInput);
      };

      // Add to DOM and trigger click
      document.body.appendChild(fileInput);
      fileInput.click();
    } else {
      toast.error('Please select a project');
    }
  };

  // PortalDropdown component
  const PortalDropdown = ({ anchorRect, children, onClose }) => {
    if (!anchorRect) return null;
    return createPortal(
      <div
        className="absolute bg-white border rounded shadow z-[1000]"
        style={{
          position: 'fixed',
          left: anchorRect.left,
          top: anchorRect.bottom,
          minWidth: anchorRect.width,
          maxHeight: '250px',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}>
        {children}
      </div>,
      document.body
    );
  };

  // Handler to open dropdown and set position
  const handleHeaderDropdownClick = (type, event) => {
    // If clicking the same dropdown that's already open, close it
    if (openDropdown === type) {
      setOpenDropdown(null);
      return;
    }

    if (type === 'criticality' && stockCriticalityHeaderRef?.current) {
      setStockCriticalityDropdownRect(stockCriticalityHeaderRef?.current?.getBoundingClientRect());
    }

    if (type === 'days' && daysFilterHeaderRef?.current) {
      setDaysFilterDropdownRect(daysFilterHeaderRef?.current?.getBoundingClientRect());
    }

    setOpenDropdown(type);
  };

  // Handler to close dropdown
  const handleCloseDropdown = () => setOpenDropdown(null);

  // Handler to select item (can be extended to filter, etc.)
  const handleSelectDropdownItem = (type, value) => {
    setOpenDropdown(null);

    if (type === 'criticality') {
      setStockCriticality(value);
    }

    if (type === 'days') {
      setDaysFilter(value);
    }
  };

  // Add a global click handler to close dropdown when clicking outside
  useEffect(() => {
    if (!openDropdown) return;
    const handleClick = () => setOpenDropdown(null);
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, [openDropdown]);

  return (
    <Layout>
      <Loader
        isVisible={isSubmitting || isProjectsLoading || isLoadingInventory || isLoadingVendors}
      />
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header
          backArrow={hasReturnTo}
          onBack={hasReturnTo ? onBack : undefined}
          data={{ name: 'Stock' }}
        />
        <div
          className={`flex justify-between items-center gap-2 fixed  z-30  bg-white px-6 py-3 pb-6  transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <div className="flex justify-between items-center w-[100%]">
            <div className="flex justify-start items-center w-[80%] gap-3 flex-wrap">
              <FormInput
                width={23}
                label={'Project'}
                register={register}
                register_key={'project'}
                isRequired={true}
                type={'dropdown'}
                options={[
                  { label: 'Select Project', value: '', disabled: true, selected: true }, // Add the "Select Project" option first
                  ...projectsForDropdown
                ]}
                validation={{ required: 'Project is required' }}
                errors={errors}
              />
              <FormInput
                width={23}
                label={'Category'}
                register={register}
                register_key={'category'}
                isRequired={false}
                type={'dropdown'}
                options={[
                  { label: 'All Categories', value: '', selected: true }, // Add the "Select Project" option first
                  ...transformedDataCategories
                ]}
                validation={{ required: 'Category is required' }}
                errors={errors}
              />
              {selectedItems.length > 0 && (
                <span className="text-sm text-gray-600 ml-2 mt-6">
                  {selectedItems.length} item(s) selected
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              {AllInventories && AllInventories?.length < 1 ? (
                <Button
                  customStyle={{
                    height: '2.5rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                  disabled={isSubmitting || isLoadingInventory}
                  type="button"
                  onClick={() => {
                    if (projectId && !isSubmitting && projectId !== '' && projectId !== null) {
                      createInventoryfn({ project_id: projectId });
                    }
                  }}>
                  Create
                </Button>
              ) : (
                <div className="flex items-center gap-3">
                  <button
                    className="flex items-center gap-2  text-[#2a565e] px-4 py-2 rounded-md border border-[#2a565e] whitespace-nowrap"
                    disabled={isSubmitting}
                    type="button"
                    onClick={() => {
                      setOpenAddMaterial(true);
                    }}>
                    <Plus size={16} />
                    <span className="font-semibold">Add Item</span>
                  </button>
                  <button
                    className="flex items-center gap-2 bg-[#2a565e] text-white px-4 py-2 rounded-md border border-[#2a565e]"
                    disabled={isSubmitting}
                    type="button"
                    onClick={() => {
                      handleDownloadTemplate();
                    }}>
                    <Download size={16} />
                    <span className="font-semibold">Template</span>
                  </button>

                  <button
                    className="flex items-center gap-2 bg-[#2a565e] text-white px-4 py-2 rounded-md border border-[#2a565e]"
                    disabled={isSubmitting}
                    type="button"
                    onClick={() => {
                      handleUploadTemplate();
                    }}>
                    <Upload size={16} />
                    <span>{isSubmitting ? 'Uploading...' : 'Upload'}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <GridWrapper width={100}>
        <div className="shadow rounded-lg h-[calc(100vh-13.5rem)]  overflow-auto mt-[12rem] relative">
          <table className="min-w-full rounded-t-xl text-left">
            <thead className="bg-gray-200 !text-gray-600 sticky top-0 z-20">
              <tr className="font-normal h-[3rem]">
                <th className="px-3 py-2 border-b font-normal">
                  <div className="flex items-start gap-2 flex-col">
                    {/* <span className="text-sm text-gray-600">Select All</span> */}
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                      className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
                    />
                  </div>
                </th>
                <th className="px-3 py-2 border-b font-normal">SKU</th>
                <th className="px-3 py-2 border-b font-normal">Name</th>
                <th className="px-3 py-2 border-b font-normal">Unit</th>
                <th className="px-3 py-2 border-b font-normal">Avg days to acquire</th>
                <th className="px-3 py-2 border-b font-normal">Safety net (%)</th>
                <th className="px-3 py-2 border-b font-normal">Safety days</th>
                <th className="px-3 py-2 border-b font-normal">Min Qty</th>
                <th className="px-3 py-2 border-b font-normal">Physical Stock</th>
                <th className="px-3 py-2 border-b font-normal">Avg rate</th>
                <th className="px-3 py-2 border-b font-normal">Stock value</th>
                <th className="px-3 py-2 border-b font-normal">In Transit</th>
                <th className="px-3 py-2 border-b font-normal">Available Stock</th>
                <th className="px-3 py-2 border-b font-normal" ref={daysFilterHeaderRef}>
                  <span>Required Stock (in {daysFilter} days)</span>
                  <button
                    className="ml-1 align-middle"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleHeaderDropdownClick('days', e);
                    }}>
                    <ChevronDown size={16} />
                  </button>
                  {openDropdown === 'days' && daysFilterDropdownRect && (
                    <PortalDropdown
                      anchorRect={daysFilterDropdownRect}
                      onClose={handleCloseDropdown}>
                      {[
                        { id: '90', value: '90', label: '90 Days' },
                        { id: '60', value: '60', label: '60 Days' },
                        { id: '30', value: '30', label: '30 Days' },
                        { id: '10', value: '10', label: '10 Days' },
                        { id: '7', value: '7', label: '7 Days' }
                      ].map((option, idx) => (
                        <div
                          key={option.id || idx}
                          className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                            option.id === daysFilter && 'bg-blue-100'
                          }`}
                          onClick={() => handleSelectDropdownItem('days', option.id)}>
                          {option.label}
                        </div>
                      ))}
                    </PortalDropdown>
                  )}
                </th>
                <th className="px-3 py-2 border-b font-normal">Consumption</th>
                <th className="px-3 py-2 border-b font-normal" ref={stockCriticalityHeaderRef}>
                  <span>Stock Criticality (%)</span>
                  <button
                    className="ml-1 align-middle"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleHeaderDropdownClick('criticality', e);
                    }}>
                    <ChevronDown size={16} />
                  </button>
                  {openDropdown === 'criticality' && stockCriticalityDropdownRect && (
                    <PortalDropdown
                      anchorRect={stockCriticalityDropdownRect}
                      onClose={handleCloseDropdown}>
                      {[
                        {
                          id: '',
                          value: '',
                          label: stockCriticality ? 'Clear Filter' : 'Select Criticality',
                          selected: true
                        },
                        {
                          id: 'excellent',
                          value: 'excellent',
                          label: 'Excellent (>150%)',
                          selected: true
                        },
                        {
                          id: 'good',
                          value: 'good',
                          label: 'Good (125-150%)',
                          selected: true
                        },
                        {
                          id: 'moderate',
                          value: 'moderate',
                          label: 'Moderate (100-125%)',
                          selected: true
                        },
                        {
                          id: 'critical',
                          value: 'critical',
                          label: 'Critical (<100%)',
                          selected: true
                        }
                      ].map((option, idx) => (
                        <div
                          key={option.id || idx}
                          className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                            option.id === '' && 'bg-gray-100'
                          } ${option.id === stockCriticality && 'bg-blue-100'}`}
                          onClick={() => handleSelectDropdownItem('criticality', option.id)}>
                          {option.label}
                        </div>
                      ))}
                    </PortalDropdown>
                  )}
                </th>
                <th className="px-3 py-2 border-b font-normal">Stock to be ordered</th>
                <th className="px-3 py-2 border-b font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              {AllInventories && AllInventories?.length > 0 ? (
                AllInventories?.map((task) => {
                  return (
                    <tr key={task.id} className="border-b bg-white h-[3.5rem]">
                      <td className="px-4 py-2 text-sm">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(task.id)}
                          onChange={() => handleItemSelect(task.id)}
                          className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-0 focus:ring-offset-0"
                        />
                      </td>
                      <td className="px-4 py-2 text-sm">{task.id || '0'}</td>
                      <td className="px-4 py-2 text-sm cursor-pointer">{task.item_name || '0'}</td>
                      <td className="px-4 py-2 text-sm">{task.unit || '0'}</td>
                      <td className="px-4 py-2 text-sm">{task.avg_acquiring_days || '0'}</td>
                      <td className="px-4 py-2 text-sm">{task.safetyNet || '0'}</td>
                      <td className="px-4 py-2 text-sm">{task.safetyDays || '0'}</td>
                      <td className="px-4 py-2 text-sm">{task.minimumQuantity || '0'}</td>
                      <td className="px-4 py-2 text-sm">{task.physicalStock || '0'}</td>
                      <td className="px-4 py-2 text-sm whitespace-nowrap">
                        {task.avg_rate == null ? '—' : `₹${Number(task.avg_rate).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}
                      </td>
                      <td className="px-4 py-2 text-sm whitespace-nowrap">
                        {task.avg_rate == null
                          ? '—'
                          : `₹${Math.round(Number(task.physicalStock || 0) * Number(task.avg_rate)).toLocaleString('en-IN')}`}
                      </td>
                      <td className="px-4 py-2 text-sm">{task.inTransitStock || '0'}</td>
                      <td className="px-4 py-2 text-sm">{task.availableStock || '0'}</td>
                      <td className="px-4 py-2 text-sm">{task.requiredStock || '0'}</td>
                      <td className="px-4 py-2 text-sm">{task.immediateConsumption || '0'}</td>
                      <td
                        className={`px-4 py-2 text-sm ${
                          (task.stock_criticality || 0) > 150
                            ? 'bg-green-100'
                            : (task.stock_criticality || 0) > 125
                              ? 'bg-yellow-100'
                              : (task.stock_criticality || 0) > 100
                                ? 'bg-orange-200'
                                : 'bg-red-400'
                        }`}>
                        {task.stock_criticality || '0'}
                      </td>
                      <td className="px-4 py-2 text-sm">{task.stock_to_be_ordered || '0'}</td>
                      <td className="px-4 py-[1.45rem] flex space-x-2 items-center text-gray-600">
                        <Pencil
                          className="w-4 h-4 cursor-pointer hover:text-blue-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(task);
                          }}
                        />
                        <span title="Stock & rate: old stock with purchase price">
                          <IndianRupee
                            className="w-4 h-4 cursor-pointer hover:text-blue-500"
                            aria-label="Stock and rate"
                            onClick={(e) => {
                              e.stopPropagation();
                              setStockEntriesItem(task);
                            }}
                          />
                        </span>
                        <div className="relative group">
                          <Button
                            customStyle={{
                              height: '1.5rem',
                              width: '3rem',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              fontSize: '0.8rem'
                            }}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRFQClick(task, e);
                            }}>
                            RFQ
                          </Button>
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-500 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                            Request For Quotation
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={18} className="text-center py-7">
                    <div className="flex flex-col items-center justify-center">
                      <img
                        src={noDataIcon}
                        alt="No Data"
                        className="w-[25rem] h-[20rem] object-cover"
                      />
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </GridWrapper>

      {/* RFQ Modal */}
      {isRFQModalOpen && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          isOpen={isRFQModalOpen}
          width={90}
          name="Request for Quotation (RFQ)"
          onClose={() => setIsRFQModalOpen(false)}>
          <RFQForm
            transformedVendors={transformedVendors}
            selectedVendors={selectedVendors}
            handleVendorSelect={handleVendorSelect}
            selectedItemsForRFQ={selectedItemsForRFQ}
            itemQuantities={itemQuantities}
            handleQuantityChange={handleQuantityChange}
            onSubmit={handleRFQSubmit}
            onCancel={handleRFQCancel}
            isSubmitDisabled={isSubmitDisabled}
            isSubmitting={isSubmitting}
            refetchVendors={refetchVendors}
            stockCriticalityFilter={daysFilter}
            CompanyAddress={getProjectById(projectId)?.company || ''}
          />
        </Modal>
      )}

      {OpenAddMaterial && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={40}
          name="Add Materials"
          onClose={() => setOpenAddMaterial(false)}>
          <AddMaterials
            onSubmit={() => {
              toast.success('Material created successfully');
              fetchInventory();
            }}
            onClose={() => setOpenAddMaterial(false)}
            onSuccess={() => fetchInventory()}
            hideBrand={true}
          />
        </Modal>
      )}

      {/* Edit Inventory Modal */}
      {stockEntriesItem && (
        <StockEntriesModal
          item={stockEntriesItem}
          onClose={() => setStockEntriesItem(null)}
          onChanged={fetchInventory}
        />
      )}
      {isEditModalOpen && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          isOpen={isEditModalOpen}
          width={50}
          name="Edit Inventory"
          onClose={handleEditModalClose}>
          <form onSubmit={handleEditSubmit(onEditSubmit)}>
            {/* Item Information Display */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Item:</span>
                <span className="text-sm text-gray-900 font-semibold">
                  {editItem?.item_name || '---'}
                  {editItem?.unit && ` (${editItem.unit})`}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-6 justify-start items-center">
              <FormInput
                type="number"
                label="Avg Acquiring Days"
                register={editRegister}
                register_key="avg_acquiring_days"
                isRequired={true}
                errors={editErrors.avg_acquiring_days}
                minLength={1}
                width={31}
              />
              <FormInput
                type="number"
                label="Safety Net (%)"
                register={editRegister}
                register_key="safetyNet"
                isRequired={true}
                errors={editErrors.safetyNet}
                width={31}
                minLength={1}
                maxLength={3}
              />

              <FormInput
                type="number"
                label="Minimum Quantity"
                register={editRegister}
                register_key="minimumQuantity"
                isRequired={true}
                errors={editErrors.minimumQuantity}
                width={31}
                minLength={1}
              />
              <FormInput
                type="number"
                label="Physical Stock"
                register={editRegister}
                register_key="physicalStock"
                isRequired={true}
                errors={editErrors.physicalStock}
                width={31}
                minLength={1}
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4 border-t mt-4">
              <Button
                customStyle={{
                  height: '2.5rem',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#6B7280',
                  color: 'white',
                  border: 'none'
                }}
                type="button"
                onClick={handleEditModalClose}>
                Cancel
              </Button>
              <Button
                customStyle={{
                  height: '2.5rem',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: 'none'
                }}
                type="submit"
                disabled={isSubmitting}>
                Save
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </Layout>
  );
};

export default Stock;
